import { useEffect } from 'react';
import type { AdventureDetail } from '../services/GameApiService';
import gameApi from '../services/GameApiService';

interface AdventureMetaTagsProps {
  adventure: AdventureDetail | null;
}

/**
 * Meta tag keys managed by this hook. We track them so we can remove them on
 * unmount / adventure change and avoid leaking the previous adventure's tags.
 */
const MANAGED_META_SELECTORS = [
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:image"]',
  'meta[property="og:image:width"]',
  'meta[property="og:image:height"]',
  'meta[property="og:image:type"]',
  'meta[property="og:url"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:image"]',
];

/**
 * Updaters for the `<title>` element and the Open Graph / Twitter meta tags.
 *
 * On the adventure detail page we override the site-wide defaults from
 * `index.html` with adventure-specific values so that social shares (LinkedIn,
 * WhatsApp, X, etc.) show the adventure's own title, synopsis and a
 * dynamically-generated cover image (hero + Selna logo).
 *
 * The pre-renderer (Puppeteer) captures the DOM *after* these mutations, so
 * the static HTML served to crawlers already contains the correct tags.
 */
export default function useAdventureMetaTags({ adventure }: AdventureMetaTagsProps) {
  useEffect(() => {
    if (!adventure) return;

    const title = `${adventure.title} — Selna`;
    const description =
      adventure.synopsis ||
      adventure.premiseExcerpt ||
      'Joga aventuras narrativas interativas com IA. As tuas escolhas moldam a história.';
    // Prefer the back-office-generated static OG image when available; fall
    // back to the runtime composition endpoint otherwise.
    const ogImageUrl =
      adventure.ogImageUrl || gameApi.adventureOgImageUrl(adventure.id);
    const canonicalUrl = `${window.location.origin}/adventure/${adventure.slug || adventure.id}`;

    // <title>
    document.title = title;

    // Helper: upsert a meta tag by selector.
    const upsertMeta = (selector: string, attr: 'property' | 'name', key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Open Graph
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', ogImageUrl);
    upsertMeta('meta[property="og:image:width"]', 'property', 'og:image:width', '1200');
    upsertMeta('meta[property="og:image:height"]', 'property', 'og:image:height', '630');
    upsertMeta('meta[property="og:image:type"]', 'property', 'og:image:type', 'image/jpeg');
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);

    // Twitter
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImageUrl);

    // Canonical link element.
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    return () => {
      // Restore site-wide defaults on unmount so other pages aren't affected.
      document.title = 'Selna Game - Aventuras Narrativas Interativas';
      for (const sel of MANAGED_META_SELECTORS) {
        const el = document.head.querySelector<HTMLMetaElement>(sel);
        if (el) el.remove();
      }
      const canonicalEl = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (canonicalEl) canonicalEl.remove();
    };
  }, [adventure]);
}
